import { useCallback, useEffect, useId, useRef, useState } from "react";
import { BsGrid3X3GapFill } from "react-icons/bs";
import AppLauncherPanel from "./AppLauncherPanel";
import useLauncherApps from "./useLauncherApps";

const FOCUSABLE =
  'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

const AppLauncher = () => {
  const apps = useLauncherApps();
  const [open, setOpen] = useState(false);
  // Keep the panel mounted through its closing animation so the pop-out plays.
  const [isMounted, setIsMounted] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  const openLauncher = () => setOpen(true);
  const closeLauncher = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open) setIsMounted(true);
  }, [open]);

  // Unmount only when the flyout's OWN closing animation ends. The guard
  // (target === currentTarget) prevents bubbling tile-cascade animationend
  // events from unmounting the panel early.
  const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (!open) setIsMounted(false);
  };

  // Move focus into the panel when it opens; restore it to the trigger on close.
  useEffect(() => {
    if (!isMounted) return;
    if (open) {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panelRef.current)?.focus();
    }
  }, [open, isMounted]);

  // Close on click outside and on Escape (restoring focus to the trigger).
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeLauncher();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeLauncher();
        triggerRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;

      // Trap focus within the panel while open.
      const focusables = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeLauncher]);

  const label = "Open app launcher";

  return (
    <div className="app-launcher" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="app-launcher-trigger"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? closeLauncher() : openLauncher())}
      >
        <BsGrid3X3GapFill aria-hidden="true" />
      </button>

      {isMounted && (
        <div
          className={`app-launcher-flyout${open ? " is-open" : " is-closing"}`}
          onAnimationEnd={handleAnimationEnd}
        >
          <AppLauncherPanel
            ref={panelRef}
            apps={apps}
            titleId={titleId}
            onSelect={closeLauncher}
          />
        </div>
      )}
    </div>
  );
};

export default AppLauncher;
